import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';

import { hospitalApi, Hospital } from '../../api/hospitalApi';

// Types
type SnackbarSeverity = 'success' | 'error' | 'info' | 'warning';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
}

// Validation schema
const schema = yup.object().shape({
  name: yup.string().required('Hospital name is required'),
  description: yup.string().optional(),
  email: yup.string().email('Invalid email').required('Email is required'),
  phoneNumber: yup.string()
    .required('Phone number is required')
    .matches(/^[0-9+\-\s]+$/, 'Invalid phone number format'),
  mobileNumber: yup.string()
    .matches(/^[0-9+\-\s]*$/, 'Invalid mobile number format')
    .optional()
    .nullable()
    .transform((value) => value === '' ? undefined : value),
  address: yup.string().optional(),
  city: yup.string().optional(),
  state: yup.string().optional(),
  country: yup.string().optional(),
  website: yup.string()
    .url('Must be a valid URL')
    .transform((value) => value === '' ? undefined : value)
    .optional(),
  registrationNumber: yup.string().optional()
});

const CompanyProfile: React.FC = () => {
  // State
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Form handling
  const { 
    control, 
    handleSubmit, 
    reset,
    formState: { errors, isDirty }
  } = useForm<Partial<Hospital>>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      description: '',
      email: '',
      phoneNumber: '',
      mobileNumber: '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      registrationNumber: '',
      taxId: '',
      website: ''
    },
  });



  // Snackbar utilities
  const showSnackbar = useCallback((message: string, severity: SnackbarSeverity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Data fetching
  const fetchCompanyData = useCallback(async () => {
    try {
      setIsLoading(true);
      const hospitalData = await hospitalApi.getCompanyProfile();
      
      // Ensure all fields have proper default values, replacing null with empty strings
      reset({
        name: hospitalData?.name || '',
        description: hospitalData?.description || '',
        email: hospitalData?.email || '',
        phoneNumber: hospitalData?.phoneNumber || '',
        mobileNumber: hospitalData?.mobileNumber || '',
        address: hospitalData?.address || '',
        city: hospitalData?.city || '',
        state: hospitalData?.state || '',
        country: hospitalData?.country || 'India', // Default to India as per schema
        registrationNumber: hospitalData?.registrationNumber || '',
        website: hospitalData?.website || ''
      });
    } catch (error) {
      console.error('Error fetching company data:', error);
      showSnackbar('Failed to load company profile. Please try again later.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [reset]);

  // Fetch data on mount
  useEffect(() => {
    fetchCompanyData();
    
    }, [fetchCompanyData]);

  // Form submission
  const onSubmit = async (data: Partial<Hospital>) => {
    if (!data) return;
    
    try {
      setIsSubmitting(true);
      // Ensure we're not sending null values to the API
      const updateData: Partial<Hospital> = Object.entries(data).reduce((acc, [key, value]) => {
        acc[key] = value === null ? '' : value;
        return acc;
      }, {} as Record<string, any>);
      
      // Update company profile
      const updatedHospital = await hospitalApi.updateCompanyProfile(updateData);
      
      // Update form with the latest data from the server, ensuring no null values
      reset({
        name: updatedHospital?.name || '',
        description: updatedHospital?.description || '',
        email: updatedHospital?.email || '',
        phoneNumber: updatedHospital?.phoneNumber || '',
        mobileNumber: updatedHospital?.mobileNumber || '',
        address: updatedHospital?.address || '',
        city: updatedHospital?.city || '',
        state: updatedHospital?.state || '',
        country: updatedHospital?.country || 'India',
        registrationNumber: updatedHospital?.registrationNumber || '',
        website: updatedHospital?.website || ''
      });
      
      showSnackbar('Company profile updated successfully', 'success');
    } catch (error) {
      console.error('Form submission failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update company profile';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Company Profile
      </Typography>
      
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Company Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    placeholder="Enter a brief description of your hospital"
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Phone Number"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="mobileNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Mobile Number"
                    error={!!errors.mobileNumber}
                    helperText={errors.mobileNumber?.message}
                  />
                )}
              />
            </Grid>
            
            {/* Address */}
            <Grid item xs={12}>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Address"
                    multiline
                    rows={2}
                    error={!!errors.address}
                    helperText={errors.address?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="City"
                    error={!!errors.city}
                    helperText={errors.city?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="State/Province"
                    error={!!errors.state}
                    helperText={errors.state?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Country"
                    error={!!errors.country}
                    helperText={errors.country?.message}
                  />
                )}
              />
            </Grid>
            
            {/* Additional Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="registrationNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Registration Number"
                    error={!!errors.registrationNumber}
                    helperText={errors.registrationNumber?.message}
                  />
                )}
              />
            </Grid>
            

              
            
            {/* Form Actions */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => window.location.reload()}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !isDirty}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyProfile;
     